import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useGetClient } from "../hooks/useGetClient.js";
import { UNAUTHORIZED_EVENT } from "../model/api.js";

const AuthContext = createContext();

const SESSION_EXPIRED_MESSAGE = 'Votre session a expiré. Veuillez vous reconnecter.';

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {

  const {
    client,
    loading: loadingClient,
    error: errorClient,
    fetchClient,
  } = useGetClient();

  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [providerName, setProviderName] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [clientInfo, setClientInfo] = useState(null);
  const [filtre, setFiltre] = useState("ouvert");
  // Message affiché sur l'écran de connexion quand l'API a fermé la session (401)
  const [authMessage, setAuthMessage] = useState(null);
  const [toast, setToast] = useState({
    visible: false,
    type: 'success',
    message: '.',
  });

  useEffect(() => {
    setClientInfo(client);
  }, [client]);

  const clearSession = useCallback(() => {
    setIsConnected(false);
    setProviderName(null);
    setUserInfo(null);
    localStorage.removeItem('client_id');
    localStorage.removeItem('userName');
  }, []);

  const logOut = useCallback(async () => {
    try {
      await fetch(window.API_URL + '/extranet/deconnexion', {
        method: 'POST',
        credentials: 'include',
      });
    } catch (e) {
      console.error('Erreur lors de la déconnexion :', e);
    }
    clearSession();
    setToast({
      visible: true,
      type: 'success',
      message: 'Déconnexion réussie',
    });
  }, [clearSession]);

  // Un 401 sur n'importe quelle requête (session expirée, abonnement sans accès
  // extranet) renvoie à l'écran de connexion avec le message de l'API.
  useEffect(() => {
    const handleUnauthorized = (event) => {
      clearSession();
      setAuthMessage(event.detail?.message || SESSION_EXPIRED_MESSAGE);
    };
    window.addEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);
  }, [clearSession]);

  const checkSession = useCallback(async () => {
    const clientId = localStorage.getItem('client_id');
    const userName = localStorage.getItem('userName');

    if (!clientId) {
      setIsConnected(false);
      setProviderName(null);
      setUserInfo(null);
      setLoading(false);
      return;
    }

    try {
      await fetchClient(clientId);
    } catch (error) {
      console.error('Erreur lors de la vérification de session :', error);
    }

    // Seul un 401 ferme la session : le listener UNAUTHORIZED_EVENT a alors déjà
    // retiré client_id. Une autre erreur (403, 5xx, réseau) laisse la session
    // ouverte ; les pages affichent leurs propres erreurs.
    if (localStorage.getItem('client_id')) {
      setUserInfo({ userName, idEntreprise: clientId });
      setIsConnected(true);
      setAuthMessage(null);
    }
    setLoading(false);
  }, [fetchClient]);

  useEffect(() => {
    checkSession();
  }, []);

  return (
    <AuthContext.Provider value={{
      isConnected,
      loading,
      providerName,
      userInfo,
      logOut,
      setToast,
      toast,
      clientInfo,
      setIsConnected,
      setProviderName,
      checkSession,
      authMessage,
      filtre,
      setFiltre
    }}>
      {children}
    </AuthContext.Provider>
  );
};
